import { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Save, Wrench, ClipboardList } from "lucide-react";
import { materialEinheiten } from "./orderHelpers";
import VoiceInputButton from "@/components/VoiceInputButton";
import BarcodeScanner from "@/components/BarcodeScanner";

export const QuickAddMaterialDialog = ({ open, onOpenChange, prefill, onSaved, initialMode = null }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxRate, setTaxRate] = useState(19);
  const [form, setForm] = useState({
    bezeichnung: "",
    artikelnummer: "",
    hersteller: "",
    einheit: "Stück",
    einkaufspreis: 0,
    verkaufspreis: 0,
    marge_prozent: 0,
    bestand: 0,
    min_bestand: 0,
    lieferant: "",
    kategorie: "",
    item_type: "material",
    is_order_specific: true, // default: Sonderbestellung (kein Lager)
  });

  // Load supplier/manufacturer/category suggestions when dialog opens
  useEffect(() => {
    if (open) {
      api.get("/api/materials/suppliers").then(r => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
      api.get("/api/materials/manufacturers").then(r => setManufacturers(r.data || [])).catch(() => setManufacturers([]));
      // Build categories from existing materials
      api.get("/api/materials").then(r => {
        const cats = Array.from(new Set((r.data || []).map(m => m.kategorie).filter(Boolean)));
        setCategories(cats.sort());
      }).catch(() => setCategories([]));
      // Tax rate from company settings
      api.get("/api/company-settings").then(r => {
        setTaxRate(r.data?.tax_rate != null ? parseFloat(r.data.tax_rate) : 19);
      }).catch(() => setTaxRate(19));
    }
  }, [open]);

  // Update form when prefill changes (when dialog opens with new material)
  useEffect(() => {
    if (prefill && open) {
      setForm({
        bezeichnung: prefill.bezeichnung || "",
        artikelnummer: prefill.artikelnummer || "",
        hersteller: prefill.hersteller || "",
        einheit: prefill.einheit || "Stück",
        einkaufspreis: prefill.einkaufspreis || 0,
        verkaufspreis: prefill.einzelpreis || 0,
        marge_prozent: prefill.marge_prozent || 0,
        bestand: parseFloat(prefill.anzahl) || 0,
        min_bestand: 0,
        lieferant: "",
        kategorie: "",
        item_type: "material",
        // If caller specified a mode, respect it; otherwise default to Sonderbestellung
        is_order_specific: initialMode === "lager" ? false : true,
      });
    }
  }, [prefill, open, initialMode]);

  const marge = form.verkaufspreis - form.einkaufspreis;
  const vkBrutto = (form.verkaufspreis || 0) * (1 + taxRate / 100);
  const isService = form.item_type === "dienstleistung";
  const isOrderSpecific = !isService && form.is_order_specific;
  const showStockFields = !isService && !isOrderSpecific;

  const handleSave = async () => {
    if (!form.bezeichnung.trim()) { toast.error("Bezeichnung eingeben"); return; }
    try {
      const payload = { ...form };
      // Sanity: order-specific never has stock
      if (isOrderSpecific) {
        payload.bestand = 0;
        payload.min_bestand = 0;
      }
      const res = await api.post("/api/materials", payload);
      if (isService) {
        toast.success(`Dienstleistung "${form.bezeichnung}" angelegt`);
      } else if (isOrderSpecific) {
        toast.success(`"${form.bezeichnung}" als Auftragsartikel gespeichert (kein Lagerbestand)`);
      } else {
        toast.success(`"${form.bezeichnung}" ins Lager übernommen (Bestand: ${form.bestand})`);
      }
      onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="quick-add-material-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Barlow Condensed" }}>
            {isService ? <Wrench className="w-5 h-5" /> : isOrderSpecific ? <ClipboardList className="w-5 h-5" /> : <Package className="w-5 h-5" />}
            {isService ? "Neue Dienstleistung" : isOrderSpecific ? "Nur für diesen Auftrag" : "Ins Lager übernehmen"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Typ-Auswahl */}
          <div className="flex gap-2 p-1 bg-muted rounded-lg" data-testid="quick-mat-type-toggle">
            <button
              type="button"
              onClick={() => setForm({ ...form, item_type: "material", einheit: form.einheit === "Pauschal" ? "Stück" : form.einheit })}
              className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium transition-colors ${!isService ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="quick-mat-toggle-material"
            >
              <Package className="w-4 h-4" /> Material
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, item_type: "dienstleistung", einheit: "Pauschal", bestand: 0, min_bestand: 0, hersteller: "", lieferant: "", is_order_specific: false })}
              className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium transition-colors ${isService ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="quick-mat-toggle-service"
            >
              <Wrench className="w-4 h-4" /> Dienstleistung
            </button>
          </div>

          {/* Auftragsartikel-Toggle: nur für Material sichtbar */}
          {!isService && (
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${isOrderSpecific ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
              data-testid="quick-mat-order-specific-toggle"
            >
              <input
                type="checkbox"
                checked={isOrderSpecific}
                onChange={e => setForm({ ...form, is_order_specific: e.target.checked, ...(e.target.checked ? { bestand: 0, min_bestand: 0 } : {}) })}
                className="mt-0.5 w-4 h-4"
                data-testid="quick-mat-order-specific-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <ClipboardList className="w-4 h-4" />
                  Nur für diesen Auftrag (kein Lagerbestand)
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {isOrderSpecific
                    ? "Speichert Artikel als Sonderbestellung. Kein Bestand, keine Inventur — aber Historie für Wiederbestellung bleibt erhalten."
                    : "Deaktivieren = normaler Lagerartikel mit Bestandsführung."}
                </p>
              </div>
            </label>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Bezeichnung *</Label>
              <div className="flex gap-2 items-center">
                <Input value={form.bezeichnung} onChange={e => setForm({...form, bezeichnung: e.target.value})} data-testid="quick-mat-bezeichnung" className="flex-1" />
                <VoiceInputButton onResult={(t) => setForm((f) => ({ ...f, bezeichnung: t }))} />
              </div>
            </div>
            <div className={isService ? "col-span-2" : ""}>
              <Label>Artikelnummer</Label>
              <div className="flex gap-2 items-center">
                <Input value={form.artikelnummer} onChange={e => setForm({...form, artikelnummer: e.target.value})} data-testid="quick-mat-artikelnummer" className="flex-1" />
                <BarcodeScanner
                  title="Artikelnummer scannen"
                  onScan={(code) => setForm((f) => ({ ...f, artikelnummer: code }))}
                />
              </div>
            </div>
            {!isService && (
              <div>
                <Label>Einheit</Label>
                <Select value={form.einheit} onValueChange={v => setForm({...form, einheit: v})}>
                  <SelectTrigger data-testid="quick-mat-einheit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {materialEinheiten.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isService && (
              <div>
                <Label>Hersteller</Label>
                <Input
                  value={form.hersteller}
                  onChange={e => setForm({...form, hersteller: e.target.value})}
                  placeholder="z.B. Bosch, MANN"
                  list="quick-manufacturer-suggestions"
                  data-testid="quick-mat-hersteller"
                />
                <datalist id="quick-manufacturer-suggestions">
                  {manufacturers.map(h => <option key={h} value={h} />)}
                </datalist>
              </div>
            )}
            <div className={isService ? "col-span-2" : ""}>
              <Label>Kategorie</Label>
              <Input
                value={form.kategorie}
                onChange={e => setForm({...form, kategorie: e.target.value})}
                placeholder="z.B. Filter, Bremsen, Öle"
                list="quick-category-suggestions"
                data-testid="quick-mat-kategorie"
              />
              <datalist id="quick-category-suggestions">
                {categories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div>
              <Label>EK (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.einkaufspreis || ""}
                onChange={e => {
                  const ek = parseFloat(e.target.value) || 0;
                  const mp = form.marge_prozent || 0;
                  const vk = mp > 0 ? Math.round(ek * (1 + mp / 100) * 100) / 100 : form.verkaufspreis;
                  setForm({...form, einkaufspreis: ek, verkaufspreis: vk});
                }} data-testid="quick-mat-ek" />
            </div>
            <div>
              <Label>Marge (%)</Label>
              <Input type="number" step="0.1" min="0" value={form.marge_prozent || ""}
                onChange={e => {
                  const mp = parseFloat(e.target.value) || 0;
                  const ek = form.einkaufspreis || 0;
                  const vk = ek > 0 ? Math.round(ek * (1 + mp / 100) * 100) / 100 : form.verkaufspreis;
                  setForm({...form, marge_prozent: mp, verkaufspreis: vk});
                }} data-testid="quick-mat-marge" />
            </div>
            <div>
              <Label>VK netto (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.verkaufspreis || ""}
                onChange={e => {
                  const vk = parseFloat(e.target.value) || 0;
                  const ek = form.einkaufspreis || 0;
                  const mp = ek > 0 ? Math.round((vk - ek) / ek * 10000) / 100 : 0;
                  setForm({...form, verkaufspreis: vk, marge_prozent: mp});
                }} data-testid="quick-mat-vk" />
            </div>
            <div>
              <Label>VK brutto (€)</Label>
              <Input type="number" step="0.01" min="0" value={vkBrutto > 0 ? vkBrutto.toFixed(2) : ""}
                onChange={e => {
                  const brutto = parseFloat(e.target.value) || 0;
                  const netto = taxRate > 0 ? Math.round(brutto / (1 + taxRate / 100) * 100) / 100 : brutto;
                  const ek = form.einkaufspreis || 0;
                  const mp = ek > 0 ? Math.round((netto - ek) / ek * 10000) / 100 : 0;
                  setForm({...form, verkaufspreis: netto, marge_prozent: mp});
                }}
                placeholder={`inkl. ${taxRate}%`}
                data-testid="quick-mat-vk-brutto" />
            </div>
            <div>
              <Label>Marge €</Label>
              <div className={`h-10 flex items-center px-3 rounded-md border text-sm font-medium ${marge >= 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                {marge >= 0 ? '+' : ''}{marge.toFixed(2)} €
              </div>
            </div>
          </div>

          {showStockFields && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Bestand (Bestellmenge)</Label>
                <Input type="number" step="1" min="0" value={form.bestand || ""}
                  onChange={e => setForm({...form, bestand: parseFloat(e.target.value) || 0})}
                  data-testid="quick-mat-bestand" />
              </div>
              <div>
                <Label>Mindestbestand</Label>
                <Input type="number" step="1" min="0" value={form.min_bestand || ""}
                  onChange={e => setForm({...form, min_bestand: parseFloat(e.target.value) || 0})}
                  data-testid="quick-mat-min-bestand" />
              </div>
            </div>
          )}

          {showStockFields && (
            <div>
              <Label>Lieferant</Label>
              <Input
                value={form.lieferant}
                onChange={e => setForm({...form, lieferant: e.target.value})}
                placeholder="z.B. Bosch"
                list="quick-supplier-suggestions"
                data-testid="quick-mat-lieferant"
              />
              <datalist id="quick-supplier-suggestions">
                {suppliers.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={handleSave} className="gap-2" data-testid="quick-mat-save">
            <Save className="w-4 h-4" /> {isService ? "Dienstleistung anlegen" : isOrderSpecific ? "Als Auftragsartikel speichern" : "Im Lager speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
